import React, { useState, useEffect } from 'react';
import ArtworkPreview from './ArtworkPreview';
import './WorkSection.css';
import { trackArtworkClick, trackLightboxOpen } from '../utils/analytics';
import { findArtworkBySlug } from '../utils/slugify';

// Fallback artworks in case JSON fetch fails
const fallbackArtworks = [
  {
    id: 1,
    image: 'Blaues_Erwachen.webp',
    title: { en: 'Blaues Erwachen', de: 'Blaues Erwachen' },
    medium: { en: 'Oil on canvas, 2023', de: 'Öl auf Leinwand, 2023' },
    description: { 
      en: 'This piece explores the interaction between light and abstract form.', 
      de: 'Dieses Werk erforscht die Interaktion zwischen Licht und abstrakter Form.' 
    },
    altText: { 
      en: 'Blaues Erwachen - abstract painting with blue tones', 
      de: 'Blaues Erwachen - abstraktes Gemälde mit Blautönen' 
    },
    dimensions: '60 x 80 cm'
  }
];

function WorkSection({ language, isVisible = true, initialArtworkSlug = null }) {
  const [artworksData, setArtworksData] = useState(fallbackArtworks);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedArtworkIndex, setSelectedArtworkIndex] = useState(0);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [clickPosition, setClickPosition] = useState(null);
  const [pendingArtworkSlug, setPendingArtworkSlug] = useState(initialArtworkSlug);
  const [openedFromDirectLink, setOpenedFromDirectLink] = useState(false);

  // Fetch artworks from JSON file on component mount
  useEffect(() => {
    const fetchArtworks = async () => {
      try {
        // Add cache-busting query param to ensure fresh data
        const response = await fetch(`/data/artworks.json?t=${Date.now()}`);
        if (response.ok) {
          const data = await response.json();
          if (Array.isArray(data) && data.length > 0) {
            setArtworksData(data);
          }
        }
      } catch (error) {
        console.error('Failed to load artworks:', error);
        // Keep using fallback artworks
      } finally {
        setIsLoading(false);
      }
    };

    fetchArtworks();
  }, []);

  // Handle opening artwork from URL (after data is loaded)
  useEffect(() => {
    if (!isLoading && pendingArtworkSlug && artworksData.length > 0) {
      const artwork = findArtworkBySlug(artworksData, pendingArtworkSlug);
      if (artwork) {
        const index = artworksData.findIndex(a => a.id === artwork.id);
        if (index !== -1) {
          setSelectedArtworkIndex(index);
          setIsPreviewOpen(true);
          setOpenedFromDirectLink(true); // Mark as opened from direct link
          document.body.style.overflow = 'hidden';
        }
      }
      setPendingArtworkSlug(null); // Clear pending slug after processing
    }
  }, [isLoading, pendingArtworkSlug, artworksData]);

  // Listen for artwork URL changes from App.js
  useEffect(() => {
    if (initialArtworkSlug && initialArtworkSlug !== pendingArtworkSlug) {
      setPendingArtworkSlug(initialArtworkSlug);
    }
  }, [initialArtworkSlug]);
  
  const handleArtworkClick = (index, position) => {
    setSelectedArtworkIndex(index);
    setClickPosition(position);
    setIsPreviewOpen(true);
    setOpenedFromDirectLink(false); // Mark as opened from gallery click
    document.body.style.overflow = 'hidden'; // Prevent background scrolling
    
    // Track artwork click/lightbox open
    const artwork = artworksData[index];
    trackArtworkClick(artwork.title.de);
    trackLightboxOpen(artwork.title.de);
  };
  
  const handleNavigate = (direction) => {
    setSelectedArtworkIndex(prevIndex => {
      if (direction === 'next' && prevIndex < artworksData.length - 1) {
        return prevIndex + 1;
      } else if (direction === 'prev' && prevIndex > 0) {
        return prevIndex - 1;
      }
      return prevIndex;
    });
  };
  
  const closePreview = () => {
    setIsPreviewOpen(false);
    document.body.style.overflow = 'auto'; // Restore scrolling
    
    // If opened from direct link, navigate to home when closing
    // If opened from gallery click, the browser back button will handle it
    if (openedFromDirectLink) {
      window.history.replaceState({ page: 'home', section: 'work' }, '', '/');
      setOpenedFromDirectLink(false);
    }
  };
  
  const translations = {
    title: {
      en: 'My Work',
      de: 'Meine Arbeiten'
    },
    subtitle: {
      en: 'Explore my latest artistic creations and projects. Click on an artwork to view details.',
      de: 'Entdecken Sie meine neuesten künstlerischen Kreationen und Projekte. Klicken Sie auf ein Kunstwerk, um Details anzuzeigen.'
    }
  };

  return (
    <section id="work" className={`section-content ${!isVisible ? 'hidden' : ''}`}>
      <div className="container">
        <h1>{translations.title[language]}</h1>
        <p>{translations.subtitle[language]}</p>
        
        <div className="gallery">
          {artworksData.map((artwork, index) => {
            // Use thumbnail for gallery, full-size for first image (LCP optimization)
            const isFirst = index === 0;
            const imageSrc = isFirst ? `/images/${artwork.image}` : `/images/thumbs/${artwork.image}`;
            
            return (
              <div className="gallery-item" key={artwork.id} onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                handleArtworkClick(index, {
                  x: rect.left,
                  y: rect.top,
                  width: rect.width,
                  height: rect.height
                });
              }}>
                <img 
                  src={imageSrc}
                  alt={artwork.altText[language]}
                  loading={isFirst ? "eager" : "lazy"}
                  fetchpriority={isFirst ? "high" : "auto"}
                  width="400"
                  height="300"
                />
                
                <div className="gallery-info">
                  <h2>{artwork.title[language]}</h2>
                  <p className="artwork-medium">{artwork.medium[language]}</p>
                  <p className="artwork-dimensions">{artwork.dimensions}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      <ArtworkPreview 
        artwork={artworksData[selectedArtworkIndex]} 
        nextArtwork={selectedArtworkIndex < artworksData.length - 1 ? artworksData[selectedArtworkIndex + 1] : null}
        prevArtwork={selectedArtworkIndex > 0 ? artworksData[selectedArtworkIndex - 1] : null}
        isOpen={isPreviewOpen} 
        onClose={closePreview}
        language={language}
        clickPosition={clickPosition}
        onNavigate={handleNavigate}
        isFirstItem={selectedArtworkIndex === 0}
        isLastItem={selectedArtworkIndex === artworksData.length - 1}
      />
    </section>
  );
}

export default WorkSection;
